import { initializeApp } from "firebase/app";
import {
  getAnalytics,
  isSupported as isAnalyticsSupported,
  logEvent,
  setAnalyticsCollectionEnabled,
  setUserId,
} from "firebase/analytics";
import { getAuth, OAuthProvider, GoogleAuthProvider } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getFunctions } from "firebase/functions";
import { getStorage } from "firebase/storage";

const firebaseConfig = {
  apiKey: "AIzaSyC53EBX4eRWZQ6ZcsvUWhs26U_gSRlBl4E",
  authDomain: "revisio-ai.firebaseapp.com",
  projectId: "revisio-ai",
  storageBucket: "revisio-ai.firebasestorage.app",
  messagingSenderId: "986928790609",
  appId: "1:986928790609:web:e886e35a1b0aa94f2c8c70",
  measurementId: "G-QNK7ZBKT9W",
};

export const firebaseApp = initializeApp(firebaseConfig);
export const auth = getAuth(firebaseApp);
export const db = getFirestore(firebaseApp);
export const functions = getFunctions(firebaseApp);
export const storage = getStorage(firebaseApp);

// Analytics is initialized only AFTER the user accepts the cookie banner
// (GDPR). `analyticsPromise` resolves to the Analytics instance once consent
// is given, or to null if the user rejects / the browser doesn't support it.
let resolveAnalytics;
export const analyticsPromise = new Promise((r) => {
  resolveAnalytics = r;
});

let analyticsResolved = false;
function markAnalyticsResolved(value) {
  if (analyticsResolved) return;
  analyticsResolved = true;
  resolveAnalytics(value);
}

// COPPA / RGPD-K gate. `null` = unknown (fail-closed, no tracking). The
// authStore calls `setTrackingEligible(true)` only once the user document
// is hydrated AND the user is in the adult bracket (>= 16). Anything
// younger keeps analytics fully disabled at the SDK level via
// setAnalyticsCollectionEnabled(false). See `utils/trackingPolicy.js`.
let trackingEligible = null;
let collectionEnforcedDisabled = false;

function enforceAnalyticsDisabledIfNeeded(analytics) {
  if (!analytics) return;
  if (trackingEligible === true) return;
  if (collectionEnforcedDisabled) return;
  try {
    setAnalyticsCollectionEnabled(analytics, false);
    collectionEnforcedDisabled = true;
  } catch (err) {
    // Old SDK versions or environments without IndexedDB can throw here;
    // we already short-circuit trackEvent/setAnalyticsUser so the worst
    // case is the SDK is initialised but never called.
    console.warn("setAnalyticsCollectionEnabled(false) failed:", err);
  }
}

/**
 * Called by authStore once the user document is hydrated. Passing `true`
 * re-enables collection (only for adults who also accepted cookies);
 * passing `false` flips the SDK-level kill switch so queued events from
 * before the user doc loaded are dropped.
 */
export async function setTrackingEligible(eligible) {
  trackingEligible = !!eligible;
  const analytics = await analyticsPromise;
  if (!analytics) return;
  try {
    setAnalyticsCollectionEnabled(analytics, trackingEligible);
    collectionEnforcedDisabled = !trackingEligible;
  } catch (err) {
    console.warn("setAnalyticsCollectionEnabled toggle failed:", err);
  }
}

export async function enableAnalytics() {
  if (analyticsResolved) return;
  const ok = await isAnalyticsSupported();
  const instance = ok ? getAnalytics(firebaseApp) : null;
  // If we already know the user isn't tracker-eligible (kid mode / under
  // 13) flip the SDK kill switch immediately so any in-flight init doesn't
  // leak a single ping.
  enforceAnalyticsDisabledIfNeeded(instance);
  markAnalyticsResolved(instance);
}

export function disableAnalytics() {
  // If consent was never given, resolve to null so trackEvent() short-circuits.
  trackingEligible = null;
  collectionEnforcedDisabled = false;
  markAnalyticsResolved(null);
}

// Analytics is only enabled after the user is authenticated AND has accepted
// cookies AND the user passes the COPPA/RGPD-K age gate (see
// `utils/trackingPolicy.js`). Triggered from authStore — not at boot — so
// unauthenticated visits never send events.

export async function trackEvent(name, params) {
  const analytics = await analyticsPromise;
  if (!analytics) return;
  // Fail-closed: if we don't know the user's age yet, drop the event.
  if (trackingEligible !== true) {
    enforceAnalyticsDisabledIfNeeded(analytics);
    return;
  }
  logEvent(analytics, name, params);
}

// Bind a Firebase Auth uid to the analytics session so reports show real
// users instead of anonymous device counts. Pass null on sign-out. The age
// gate applies here too — we never leak a UID to Analytics for a minor.
export async function setAnalyticsUser(uid) {
  const analytics = await analyticsPromise;
  if (!analytics) return;
  if (trackingEligible !== true) {
    enforceAnalyticsDisabledIfNeeded(analytics);
    setUserId(analytics, null);
    return;
  }
  setUserId(analytics, uid || null);
}

export function createAppleProvider() {
  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");
  return provider;
}

export function createGoogleProvider() {
  const provider = new GoogleAuthProvider();
  provider.addScope("email");
  provider.addScope("profile");
  return provider;
}
